import { useEffect, useState } from "react";
import { supabase } from "@/src/integrations/supabase/client";
import { usePortalAuth } from "@/lib/contexts/PortalAuthContext";

export type PartnerAccessTier = "temp" | "full";

interface State {
  tier: PartnerAccessTier | null;
  partnerId: string | null;
  loading: boolean;
}

/**
 * Resolves the current portal user's partner access tier via membership.
 * A user can be the account owner OR an invited team member; either way the
 * tier is inherited from the shared supplier account. Returns `loading: true`
 * until a definitive answer is known.
 */
export function usePartnerAccessTier(): State {
  const { user, loading: authLoading } = usePortalAuth();
  const [state, setState] = useState<State>({ tier: null, partnerId: null, loading: true });

  useEffect(() => {
    let cancelled = false;
    if (authLoading) return;
    if (!user) {
      setState({ tier: null, partnerId: null, loading: false });
      return;
    }

    (async () => {
      // Resolve the partner account this user belongs to (owner or member).
      const { data: partnerId, error: pidErr } = await supabase.rpc(
        "current_user_partner_id",
        { _uid: user.id }
      );

      if (cancelled) return;

      if (pidErr || !partnerId) {
        // No active membership - fall back to legacy single-owner lookup.
        const { data, error } = await supabase
          .from("partners")
          .select("id, access_tier")
          .eq("user_id", user.id)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();
        if (cancelled) return;
        if (error) {
          setState({ tier: "temp", partnerId: null, loading: false });
          return;
        }
        setState({
          tier: (data?.access_tier as PartnerAccessTier) ?? "temp",
          partnerId: data?.id ?? null,
          loading: false,
        });
        return;
      }

      const { data, error } = await supabase
        .from("partners")
        .select("id, access_tier")
        .eq("id", partnerId as string)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        console.error("Failed to load partner access tier:", error);
        setState({ tier: "temp", partnerId: partnerId as string, loading: false });
        return;
      }

      setState({
        tier: (data?.access_tier as PartnerAccessTier) ?? "temp",
        partnerId: (data?.id as string) ?? (partnerId as string),
        loading: false,
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  return state;
}
